import { Injectable, inject } from '@angular/core';
import { of, catchError, map } from 'rxjs';

import { appActions } from '../../../app.actions';
import { BaseEffects } from '../../../base.effects';
import { ENTER_PHONE_NUMBER_DIALOG } from '../../../tokens/auth-dialog.tokens';
import { NotificationActions } from '../../notifications/notification.actions';
import { AuthStateSelectors } from '../auth.selectors';
import { PhoneNumberActions } from '../change-phone-number/change-phone-number.actions';
import { AmplifyService } from '../services/amplify.service';

import { LoginActions, LoginMfaActions } from './login.actions';

@Injectable()
export class LoginEnableMfaEffects extends BaseEffects {
  private readonly amplifyService = inject(AmplifyService);
  private readonly openEnterPhoneNumber = inject(ENTER_PHONE_NUMBER_DIALOG);

  loginSetupMfa$ = this.switchMapEffect({
    action: LoginActions.logInSetupMfa,
    switchMapFn: () =>
      this.amplifyService.getUserAttributes().pipe(
        map(attributes => {
          const hasPhone = attributes?.phone_number;
          const isVerified = attributes?.phone_number_verified;
          if (hasPhone && isVerified) {
            return LoginMfaActions.enable();
          }
          return LoginMfaActions.setup();
        }),
        // Errors should never happen, but if it does, we'll assume
        // we need to get and confirm their phone number
        catchError(() => of(LoginMfaActions.setup()))
      ),
  });

  enableMFAOnLogin$ = this.switchMapEffect({
    action: LoginMfaActions.enable,
    switchMapFn: () =>
      this.amplifyService.enableSmsMfa().pipe(
        map(() => LoginMfaActions.enableSuccess()),
        catchError((error: unknown) => of(LoginMfaActions.enableFailure({ error })))
      ),
  });

  setupMFAOnLogin$ = this.switchMapWithConcatEffect({
    action: LoginMfaActions.setup,
    concatLatestFromFn: () => this.store.select(AuthStateSelectors.mfaSetupInProgress),
    switchMapFn: (_, mfaSetupInProgress) =>
      mfaSetupInProgress
        ? of(appActions.noop())
        : this.amplifyService.getUserAttributes().pipe(
            map(attributes => {
              if (attributes.phone_number) {
                return PhoneNumberActions.sendVerification({ workflow: 'LOGIN', isResend: false });
              }
              return LoginMfaActions.setupOpenPhoneDialog();
            })
          ),
  });

  setupMFAOnLoginOpenPhoneDialog$ = this.exhaustMapEffect({
    action: LoginMfaActions.setupOpenPhoneDialog,
    exhaustMapFn: () =>
      this.openEnterPhoneNumber({
        disableClose: true,
        data: { title: 'Enter your Mobile Device number' },
      })
        .afterClosed()
        .pipe(
          map(({ phoneNumber }) => {
            return PhoneNumberActions.startUpdateWorkflow({ phoneNumber, workflow: 'LOGIN' });
          })
        ),
  });

  enableMFA_afterPhoneVerified$ = this.mapEffect({
    action: PhoneNumberActions.confirmationCodeConfirmed,
    filterFn: ({ workflow }) => workflow === 'LOGIN',
    mapFn: () => LoginMfaActions.enable(),
  });

  enableMFAOnLogin_Success$ = this.switchMapEffect({
    action: LoginMfaActions.enableSuccess,
    switchMapFn: () =>
      this.amplifyService
        .getTokens()
        .pipe(map(tokens => LoginActions.logInWithToken({ idToken: tokens.idToken.toString() }))),
  });

  // The phone is verified before enable runs, so a silent failure leaves a verified number with no enabled factor.
  enableMFAOnLogin_Failure$ = this.mapEffect({
    action: LoginMfaActions.enableFailure,
    mapFn: ({ error }) =>
      NotificationActions.notifyError({
        error,
        message: 'We could not finish turning on multi-factor authentication. Please try again.',
      }),
  });
}
